import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, TrendingUp, Users, FileSignature, Briefcase, MessageSquare, Target } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { supabase } from '../../lib/supabase';
import styles from './analytics.module.css';

interface Kpi { label: string; value: string; hint: string; icon: LucideIcon }
interface MonthRevenue { month: string; total: number }
interface SourceCount { source_id: string | null; count: number; label: string }
interface TopCustomer { id: string; name: string; total_revenue: number; quote_count: number }
interface PipelineStage { status: string; label: string; count: number }
interface StuckLead { id: string; full_name: string; status: string; days_stuck: number }
interface BotFunnelStage { state: string; label: string; count: number }
interface AtRiskCustomer { id: string; name: string; days_since_contact: number }

const LEAD_STATUS_ORDER = ['new', 'contacted', 'qualified', 'quoted', 'converted'] as const;
const LEAD_STATUS_LABELS: Record<string, string> = {
  new: 'חדש',
  contacted: 'בקשר',
  qualified: 'מוסמך',
  quoted: 'נשלחה הצעה',
  converted: 'הומר',
  disqualified: 'נפסל',
};

// Bot funnel: normalize both v1 uppercase and v2 lowercase-kebab into a
// canonical key. Order reflects the actual conversation flow.
const BOT_FUNNEL_ORDER = [
  'greeting',
  'awaiting-pain',
  'awaiting-name',
  'identity-collection',
  'qualification',
  'service-discovery',
  'idle-after-handoff',
  'human-takeover',
  'dormant',
  'closed',
] as const;
const BOT_FUNNEL_LABELS: Record<string, string> = {
  'greeting': 'פתיחה',
  'awaiting-pain': 'תיאור צורך',
  'awaiting-name': 'שם',
  'identity-collection': 'איסוף פרטים',
  'qualification': 'הסמכה',
  'service-discovery': 'בירור שירות',
  'idle-after-handoff': 'מסירה',
  'human-takeover': 'שליטה ידנית',
  'dormant': 'רדום',
  'closed': 'סגור',
};

function normalizeBotState(state: string): string {
  // Map both casings to canonical lowercase-kebab.
  const lower = state.toLowerCase();
  if (lower.includes('_')) return lower.replace(/_/g, '-');
  return lower;
}

function formatILS(amount: number): string {
  return `₪ ${new Intl.NumberFormat('he-IL', { maximumFractionDigits: 0 }).format(amount)}`;
}

function monthLabel(yyyymm: string): string {
  const [year, month] = yyyymm.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return format(date, 'MMM yy', { locale: he });
}

export function AnalyticsPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;

  const [kpis, setKpis] = useState<Kpi[] | null>(null);
  const [monthlyRevenue, setMonthlyRevenue] = useState<MonthRevenue[]>([]);
  const [sourcesFunnel, setSourcesFunnel] = useState<SourceCount[]>([]);
  const [topCustomers, setTopCustomers] = useState<TopCustomer[]>([]);
  const [leadPipeline, setLeadPipeline] = useState<PipelineStage[]>([]);
  const [stuckLeads, setStuckLeads] = useState<StuckLead[]>([]);
  const [botFunnel, setBotFunnel] = useState<BotFunnelStage[]>([]);
  const [atRiskCustomers, setAtRiskCustomers] = useState<AtRiskCustomer[]>([]);

  useEffect(() => {
    if (!orgId) return;
    const cancel = { current: false };
    const since30 = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const since365 = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString();

    void (async () => {
      const [leadsAll, leadsConverted, leadsHot, quotesAll, quotesAccepted, quotesAcceptedYear, customers, projectsActive, projectsCompleted, botConvs, focusActive] = await Promise.all([
        supabase.from('leads').select('id, source_id, created_at, status', { count: 'exact' }).eq('organization_id', orgId).is('deleted_at', null),
        supabase.from('leads').select('id', { count: 'exact', head: true }).eq('organization_id', orgId).eq('status', 'converted').is('deleted_at', null),
        supabase.from('leads').select('id', { count: 'exact', head: true }).eq('organization_id', orgId).eq('score_class', 'hot').is('deleted_at', null),
        supabase.from('quotes').select('id', { count: 'exact', head: true }).eq('organization_id', orgId).is('deleted_at', null),
        supabase.from('quotes').select('value_amount, accepted_at, customer_id').eq('organization_id', orgId).eq('status', 'accepted').is('deleted_at', null).gte('accepted_at', since30),
        supabase.from('quotes').select('value_amount, accepted_at, customer_id').eq('organization_id', orgId).eq('status', 'accepted').is('deleted_at', null).gte('accepted_at', since365),
        supabase.from('companies').select('id, name', { count: 'exact' }).eq('organization_id', orgId).is('deleted_at', null),
        supabase.from('projects').select('id', { count: 'exact', head: true }).eq('organization_id', orgId).eq('status', 'active').is('deleted_at', null),
        supabase.from('projects').select('id', { count: 'exact', head: true }).eq('organization_id', orgId).eq('status', 'completed').is('deleted_at', null),
        supabase.from('bot_conversations').select('id', { count: 'exact', head: true }).eq('organization_id', orgId).is('deleted_at', null).gte('created_at', since30),
        supabase.from('focus_tasks').select('id', { count: 'exact', head: true }).eq('organization_id', orgId).in('bucket', ['today', 'week']).is('deleted_at', null).is('completed_at', null).then((r) => r, () => ({ count: 0 })),
      ]);

      if (cancel.current) return;

      // KPIs
      const leadsTotal = leadsAll.count ?? 0;
      const convertedTotal = leadsConverted.count ?? 0;
      const convRate = leadsTotal > 0 ? Math.round((convertedTotal / leadsTotal) * 100) : 0;
      const monthRev = (quotesAccepted.data ?? []).reduce((s, q) => s + q.value_amount, 0);

      setKpis([
        { label: 'יחס המרת לידים', value: `${String(convRate)}%`, hint: `${String(convertedTotal)} מתוך ${String(leadsTotal)}`, icon: TrendingUp },
        { label: 'הכנסות החודש', value: formatILS(monthRev), hint: `${String(quotesAccepted.data?.length ?? 0)} הצעות אושרו`, icon: BarChart3 },
        { label: 'לידים חמים', value: String(leadsHot.count ?? 0), hint: 'score ≥ 80', icon: TrendingUp },
        { label: 'לידים כולל', value: String(leadsTotal), hint: 'פתוחים + מומרים + פסולים', icon: Users },
        { label: 'הצעות מחיר', value: String(quotesAll.count ?? 0), hint: 'כל הסטטוסים', icon: FileSignature },
        { label: 'לקוחות', value: String(customers.count ?? 0), hint: 'כולל פוטנציאליים', icon: Users },
        { label: 'פרויקטים פעילים', value: String(projectsActive.count ?? 0), hint: `${String(projectsCompleted.count ?? 0)} הושלמו`, icon: Briefcase },
        { label: 'שיחות בוט · 30י׳', value: String(botConvs.count ?? 0), hint: 'WhatsApp', icon: MessageSquare },
        { label: 'משימות פוקוס פתוחות', value: String(focusActive.count ?? 0), hint: 'היום + השבוע', icon: Target },
      ]);

      // Monthly revenue from accepted_at (last 12 months)
      const byMonth: Record<string, number> = {};
      const now = new Date();
      for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        byMonth[key] = 0;
      }
      (quotesAcceptedYear.data ?? []).forEach((q) => {
        if (!q.accepted_at) return;
        const d = new Date(q.accepted_at);
        const key = `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (key in byMonth) byMonth[key] = (byMonth[key] ?? 0) + q.value_amount;
      });
      setMonthlyRevenue(Object.entries(byMonth).map(([month, total]) => ({ month, total })));

      // Top customers by accepted revenue
      const revByCustomer: Record<string, { total: number; count: number }> = {};
      (quotesAcceptedYear.data ?? []).forEach((q) => {
        if (!q.customer_id) return;
        const bucket = revByCustomer[q.customer_id] ?? { total: 0, count: 0 };
        bucket.total += q.value_amount;
        bucket.count += 1;
        revByCustomer[q.customer_id] = bucket;
      });
      const customerById: Record<string, string> = {};
      (customers.data ?? []).forEach((c) => { customerById[c.id] = c.name; });
      const top = Object.entries(revByCustomer)
        .map(([id, { total, count }]) => ({ id, name: customerById[id] ?? '(נמחק)', total_revenue: total, quote_count: count }))
        .sort((a, b) => b.total_revenue - a.total_revenue)
        .slice(0, 5);
      setTopCustomers(top);

      // Sources funnel
      const { data: sourcesList } = await supabase
        .from('lead_sources')
        .select('id, name')
        .eq('organization_id', orgId)
        .is('deleted_at', null);
      const sourceNameById: Record<string, string> = {};
      (sourcesList ?? []).forEach((s) => { sourceNameById[s.id] = s.name; });

      const countBySource: Record<string, number> = {};
      (leadsAll.data ?? []).forEach((l) => {
        const key = l.source_id ?? 'unknown';
        countBySource[key] = (countBySource[key] ?? 0) + 1;
      });
      const funnel = Object.entries(countBySource)
        .map(([id, count]) => ({
          source_id: id === 'unknown' ? null : id,
          count,
          label: id === 'unknown' ? 'לא ידוע' : (sourceNameById[id] ?? id),
        }))
        .sort((a, b) => b.count - a.count);
      setSourcesFunnel(funnel);

      // ─── Lead pipeline funnel (Phase 5 §7) ─────────────────────────
      const pipelineCounts: Record<string, number> = {};
      (leadsAll.data ?? []).forEach((l) => {
        pipelineCounts[l.status] = (pipelineCounts[l.status] ?? 0) + 1;
      });
      setLeadPipeline(
        LEAD_STATUS_ORDER.map((status) => ({
          status,
          label: LEAD_STATUS_LABELS[status] ?? status,
          count: pipelineCounts[status] ?? 0,
        })),
      );

      // ─── Stuck leads (Phase 5 §7) — non-terminal status, updated > 7d ago
      const sevenDaysAgoIso = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data: stuck } = await supabase
        .from('leads')
        .select('id, full_name, status, updated_at')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .not('status', 'in', '("converted","disqualified")')
        .lt('updated_at', sevenDaysAgoIso)
        .order('updated_at', { ascending: true })
        .limit(10);
      const now2 = Date.now();
      setStuckLeads(
        (stuck ?? []).map((l) => ({
          id: l.id,
          full_name: l.full_name,
          status: l.status,
          days_stuck: Math.floor((now2 - new Date(l.updated_at).getTime()) / (24 * 60 * 60 * 1000)),
        })),
      );

      // ─── Bot conversation funnel (Phase 5 §7) ──────────────────────
      const { data: botStates } = await supabase
        .from('bot_conversations')
        .select('state')
        .eq('organization_id', orgId)
        .is('deleted_at', null);
      const botCounts: Record<string, number> = {};
      (botStates ?? []).forEach((row) => {
        const canon = normalizeBotState(row.state);
        botCounts[canon] = (botCounts[canon] ?? 0) + 1;
      });
      setBotFunnel(
        BOT_FUNNEL_ORDER.filter((s) => (botCounts[s] ?? 0) > 0).map((state) => ({
          state,
          label: BOT_FUNNEL_LABELS[state] ?? state,
          count: botCounts[state] ?? 0,
        })),
      );

      // ─── At-risk customers (Phase 5 §7) — no quote in 30d
      const thirtyDaysAgoIso = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data: recentQuoteCustomers } = await supabase
        .from('quotes')
        .select('customer_id')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .gte('created_at', thirtyDaysAgoIso);
      const recentSet = new Set(
        (recentQuoteCustomers ?? [])
          .map((q) => q.customer_id)
          .filter((id): id is string => id !== null),
      );
      // Customers with at least one historical quote but none in last 30d.
      const { data: historicalQuoteCustomers } = await supabase
        .from('quotes')
        .select('customer_id, created_at')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .lt('created_at', thirtyDaysAgoIso)
        .order('created_at', { ascending: false });
      const lastContactByCustomer = new Map<string, string>();
      (historicalQuoteCustomers ?? []).forEach((q) => {
        if (!q.customer_id) return;
        if (!lastContactByCustomer.has(q.customer_id)) {
          lastContactByCustomer.set(q.customer_id, q.created_at);
        }
      });
      const atRisk: AtRiskCustomer[] = [];
      for (const [id, lastIso] of lastContactByCustomer) {
        if (recentSet.has(id)) continue;
        const days = Math.floor((Date.now() - new Date(lastIso).getTime()) / (24 * 60 * 60 * 1000));
        atRisk.push({
          id,
          name: customerById[id] ?? '(נמחק)',
          days_since_contact: days,
        });
      }
      atRisk.sort((a, b) => b.days_since_contact - a.days_since_contact);
      setAtRiskCustomers(atRisk.slice(0, 10));
    })();

    return () => { cancel.current = true; };
  }, [orgId]);

  const maxRevenue = Math.max(1, ...monthlyRevenue.map((m) => m.total));
  const maxSource = Math.max(1, ...sourcesFunnel.map((s) => s.count));
  const maxPipeline = Math.max(1, ...leadPipeline.map((s) => s.count));
  const maxBotFunnel = Math.max(1, ...botFunnel.map((s) => s.count));
  const totalLeads = leadPipeline.reduce((s, p) => s + p.count, 0);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h1>אנליטיקה</h1>
          <p className={styles.sub}>קצב התנועה האמיתי בעסק — KPI חיים, הכנסות 12 חודשים, פילוח מקורות, top לקוחות. כל המספרים נשלפים live מ-Supabase.</p>
        </div>
        <BarChart3 size={32} className={styles.headIcon} aria-hidden="true" />
      </header>

      {kpis === null ? (
        <p className={styles.notice}>טוען נתונים...</p>
      ) : (
        <>
          {/* KPI grid */}
          <div className={styles.grid}>
            {kpis.map((kpi) => {
              const Icon = kpi.icon;
              return (
                <div key={kpi.label} className={styles.card}>
                  <div className={styles.cardHead}>
                    <Icon size={18} aria-hidden="true" className={styles.cardIcon} />
                    <span className={styles.label}>{kpi.label}</span>
                  </div>
                  <div className={styles.value}><bdi>{kpi.value}</bdi></div>
                  <div className={styles.hint}>{kpi.hint}</div>
                </div>
              );
            })}
          </div>

          {/* Monthly revenue chart */}
          <div className={styles.section}>
            <h3>הכנסות 12 חודשים אחרונים</h3>
            {monthlyRevenue.every((m) => m.total === 0) ? (
              <p className={styles.notice}>אין עדיין הצעות מאושרות.</p>
            ) : (
              <div className={styles.chart}>
                {monthlyRevenue.map((m) => {
                  const heightPct = (m.total / maxRevenue) * 100;
                  return (
                    <div key={m.month} className={styles.bar}>
                      <div className={styles.barValue}><bdi>{m.total > 0 ? formatILS(m.total) : ''}</bdi></div>
                      <div className={styles.barFill} style={{ height: `${String(heightPct)}%` }} />
                      <div className={styles.barLabel}>{monthLabel(m.month)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Top 5 customers */}
          <div className={styles.twoCol}>
            <div className={styles.section}>
              <h3>Top 5 לקוחות · 12 חודשים</h3>
              {topCustomers.length === 0 ? (
                <p className={styles.notice}>אין לקוחות מאושרים השנה.</p>
              ) : (
                <ol className={styles.topList}>
                  {topCustomers.map((c, idx) => (
                    <li key={c.id}>
                      <span className={styles.rankBadge}>{String(idx + 1)}</span>
                      <span className={styles.topName}>{c.name}</span>
                      <span className={styles.topQuotes}>{String(c.quote_count)} הצעות</span>
                      <span className={styles.topTotal}><bdi>{formatILS(c.total_revenue)}</bdi></span>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            {/* Lead pipeline funnel */}
            <div className={styles.section}>
              <h3>צינור לידים</h3>
              {totalLeads === 0 ? (
                <p className={styles.notice}>אין לידים עדיין.</p>
              ) : (
                <div className={styles.sourcesFunnel}>
                  {leadPipeline.map((p) => {
                    const widthPct = (p.count / maxPipeline) * 100;
                    const sharePct = totalLeads > 0 ? Math.round((p.count / totalLeads) * 100) : 0;
                    return (
                      <div key={p.status} className={styles.sourceRow}>
                        <span className={styles.sourceLabel}>{p.label}</span>
                        <div className={styles.sourceBar}>
                          <div className={styles.sourceBarFill} style={{ width: `${String(widthPct)}%` }} />
                          <span className={styles.sourceCount}>
                            <span className="num">{String(p.count)}</span> · {String(sharePct)}%
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Sources funnel. Populated only for leads created after migration
                0016 wired leads.source_id — before that it was always empty
                because nothing ever wrote the column. Full revenue-by-source
                breakdown lives on /analytics/attribution. */}
            <div className={styles.section}>
              <h3>
                פילוח מקורות לידים{' '}
                <Link to="/analytics/attribution" className={styles.sectionLink}>
                  מאיפה בא הכסף ←
                </Link>
              </h3>
              {sourcesFunnel.length === 0 ? (
                <p className={styles.notice}>אין לידים עדיין.</p>
              ) : (
                <div className={styles.sourcesFunnel}>
                  {sourcesFunnel.map((s) => {
                    const widthPct = (s.count / maxSource) * 100;
                    return (
                      <div key={s.source_id ?? 'unknown'} className={styles.sourceRow}>
                        <span className={styles.sourceLabel}>{s.label}</span>
                        <div className={styles.sourceBar}>
                          <div className={styles.sourceBarFill} style={{ width: `${String(widthPct)}%` }} />
                          <span className={styles.sourceCount}>{String(s.count)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Bot conversation funnel + Stuck leads */}
          <div className={styles.twoCol}>
            <div className={styles.section}>
              <h3>צינור הבוט · WhatsApp</h3>
              {botFunnel.length === 0 ? (
                <p className={styles.notice}>אין שיחות בוט עדיין.</p>
              ) : (
                <div className={styles.sourcesFunnel}>
                  {botFunnel.map((s) => {
                    const widthPct = (s.count / maxBotFunnel) * 100;
                    return (
                      <div key={s.state} className={styles.sourceRow}>
                        <span className={styles.sourceLabel}>{s.label}</span>
                        <div className={styles.sourceBar}>
                          <div className={styles.sourceBarFill} style={{ width: `${String(widthPct)}%` }} />
                          <span className={styles.sourceCount}><span className="num">{String(s.count)}</span></span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className={styles.section}>
              <h3>לידים תקועים · מעל 7 ימים</h3>
              {stuckLeads.length === 0 ? (
                <p className={styles.notice}>אין לידים תקועים. כל הכבוד.</p>
              ) : (
                <ol className={styles.topList}>
                  {stuckLeads.map((l, idx) => (
                    <li key={l.id}>
                      <span className={styles.rankBadge}>{String(idx + 1)}</span>
                      <span className={styles.topName}>{l.full_name}</span>
                      <span className={styles.topQuotes}>{LEAD_STATUS_LABELS[l.status] ?? l.status}</span>
                      <span className={styles.topTotal}><span className="num">{String(l.days_stuck)}</span> ימים</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>

          {/* At-risk customers */}
          <div className={styles.section}>
            <h3>לקוחות בסיכון · ללא מגע 30 ימים</h3>
            {atRiskCustomers.length === 0 ? (
              <p className={styles.notice}>אין לקוחות בסיכון. הכל מתואם.</p>
            ) : (
              <ol className={styles.topList}>
                {atRiskCustomers.map((c, idx) => (
                  <li key={c.id}>
                    <span className={styles.rankBadge}>{String(idx + 1)}</span>
                    <span className={styles.topName}>{c.name}</span>
                    <span className={styles.topTotal}><span className="num">{String(c.days_since_contact)}</span> ימים</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </>
      )}
    </div>
  );
}
